import React, { useState, useEffect } from 'react';
import adaLogo from '../ada-logo.svg';
import { useNavigate } from 'react-router';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const passwordRequirements = [
  { label: 'At least 8 characters', test: (pwd: string) => pwd.length >= 8 },
  { label: 'At least one uppercase letter', test: (pwd: string) => /[A-Z]/.test(pwd) },
  { label: 'At least one number', test: (pwd: string) => /[0-9]/.test(pwd) },
  { label: 'At least one special character', test: (pwd: string) => /[^A-Za-z0-9]/.test(pwd) },
];

const RegisterPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState<string | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('access_token')) {
      navigate('/analyze', { replace: true });
    }
  }, [navigate]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setError(undefined);
    setResendError(undefined);
    setResent(false);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!username || !email || !password || !confirmPassword) {
      setError('All fields are required.');
      return;
    }
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!passwordRequirements.every(req => req.test(password))) {
      setError('Password does not meet all requirements.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('email', email);
      formData.append('password', password);
      const res = await fetch(`${API_URL}/register/`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Registration failed.');
      }
    } catch (e) {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendError(undefined);
    setResent(false);
    try {
      const res = await fetch(`${API_URL}/resend_verification/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.success) {
        setResent(true);
      } else {
        setResendError(data.error || 'Failed to resend verification email.');
      }
    } catch (e) {
      setResendError('Network error.');
    } finally {
      setResendLoading(false);
    }
  };

  // Password strength calculation (simple)
  const checkStrength = (pwd: string) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    setPasswordStrength(score);
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailValid = emailRegex.test(email);
  const passwordsMatch = password === confirmPassword;
  const passwordValid = passwordRequirements.every(req => req.test(password));
  const isFormValid = username && email && isEmailValid && password && confirmPassword && passwordsMatch && passwordValid;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#e3eafc' }}>
      <a href="/">
        <img src={adaLogo} alt="ADA Logo" style={{ width: 72, marginBottom: 18, cursor: 'pointer' }} />
      </a>
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px #039be51a', padding: '32px 32px 24px 32px', minWidth: 320, maxWidth: 360 }}>
        <h2 style={{ color: '#039be5', fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>Create your ADA account</h2>
        <p style={{ color: '#555', fontSize: '1rem', marginBottom: 18, textAlign: 'center' }}>Sign up to analyze and ask questions about your documents.</p>
        {success ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, padding: '18px 0' }}>
            <div style={{ background: '#26c6da', color: '#fff', borderRadius: 8, padding: '18px 32px', fontWeight: 600, fontSize: '1.1rem', textAlign: 'center', marginBottom: 10 }}>
              A verification email has been sent to <span style={{ fontWeight: 700 }}>{email}</span>.<br />
              Please check your inbox and click the link to verify your account.<br />
              <span style={{ fontWeight: 400, fontSize: '0.98rem', color: '#e3eafc', display: 'block', marginTop: 8 }}>
                (If you don't see it, check your spam folder.)
              </span>
            </div>
            <button
              onClick={handleResend}
              disabled={resendLoading}
              style={{
                background: '#039be5',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: '1.08rem',
                boxShadow: '0 2px 8px #29b6f633',
                cursor: resendLoading ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
                opacity: resendLoading ? 0.7 : 1,
                padding: '14px 32px',
                minWidth: 220,
                margin: '12px 0 8px 0',
                letterSpacing: 0.2,
                display: 'block',
                textAlign: 'center',
              }}
            >
              {resendLoading ? 'Resending...' : 'Resend verification email'}
            </button>
            {resent && (
              <div style={{ color: '#43a047', background: '#e3fcec', borderRadius: 6, padding: '8px 12px', fontWeight: 500, fontSize: '0.98rem', marginTop: 2 }}>
                Verification email resent!
              </div>
            )}
            {resendError && (
              <div style={{ color: '#d32f2f', background: '#ffeaea', borderRadius: 6, padding: '8px 12px', fontWeight: 500, fontSize: '0.98rem', marginTop: 2 }}>{resendError}</div>
            )}
            <div style={{ marginTop: 18, color: '#555', fontSize: '1rem' }}>
              Already verified? <a href="/login" style={{ color: '#039be5', fontWeight: 600, textDecoration: 'none' }}>Sign In</a>
            </div>
          </div>
        ) : (
          <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label htmlFor="username" style={{ fontWeight: 500, color: '#222' }}>Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoComplete="username"
                disabled={loading}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #b3c6e0', fontSize: '1rem' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label htmlFor="email" style={{ fontWeight: 500, color: '#222' }}>Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                disabled={loading}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #b3c6e0', fontSize: '1rem' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label htmlFor="password" style={{ fontWeight: 500, color: '#222' }}>Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => { setPassword(e.target.value); checkStrength(e.target.value); }}
                autoComplete="new-password"
                disabled={loading}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #b3c6e0', fontSize: '1rem' }}
              />
              <div style={{ margin: '8px 0 4px 0' }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.98rem' }}>
                  {passwordRequirements.map((req, idx) => (
                    <li key={idx} style={{ color: req.test(password) ? '#43a047' : '#d32f2f', fontWeight: req.test(password) ? 600 : 400 }}>
                      {req.test(password) ? '✔' : '✖'} {req.label}
                    </li>
                  ))}
                </ul>
              </div>
              <div style={{ height: 8, marginTop: 2, marginBottom: 8 }}>
                <div style={{
                  width: `${(passwordStrength / 4) * 100}%`,
                  height: 8,
                  background: passwordStrength < 2 ? '#d32f2f' : passwordStrength < 3 ? '#fbc02d' : '#43a047',
                  borderRadius: 4,
                  transition: 'width 0.2s',
                }} />
              </div>
              <div style={{ fontSize: '0.92rem', color: '#888', marginBottom: 4 }}>
                Password strength: {['Very weak', 'Weak', 'Medium', 'Strong', 'Very strong'][passwordStrength] || 'Very weak'}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label htmlFor="confirmPassword" style={{ fontWeight: 500, color: '#222' }}>Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setConfirmPasswordTouched(true); }}
                autoComplete="new-password"
                disabled={loading}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #b3c6e0', fontSize: '1rem' }}
              />
              {confirmPasswordTouched && confirmPassword && !passwordsMatch && (
                <div style={{ color: '#d32f2f', background: '#ffeaea', borderRadius: 6, padding: '6px 10px', fontWeight: 500, fontSize: '0.95rem', marginTop: 2 }}>
                  Passwords do not match.
                </div>
              )}
            </div>
            {error && <div style={{ color: '#d32f2f', background: '#ffeaea', borderRadius: 6, padding: '8px 12px', fontWeight: 500, fontSize: '0.98rem', marginTop: 2 }}>{error}</div>}
            {touched && (!username || !email || !password || !confirmPassword) && (
              <div style={{ color: '#d32f2f', background: '#ffeaea', borderRadius: 6, padding: '8px 12px', fontWeight: 500, fontSize: '0.98rem', marginTop: 2 }}>
                All fields are required.
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !isFormValid}
              style={{
                marginTop: 8,
                padding: '12px 0',
                background: '#039be5',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: '1.08rem',
                boxShadow: '0 2px 8px #29b6f633',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>
        )}
      </div>
      <div style={{ marginTop: 18, color: '#555', fontSize: '1rem' }}>
        Already have an account? <a href="/login" style={{ color: '#039be5', fontWeight: 600, textDecoration: 'none' }}>Sign In</a>
      </div>
      {showToast && (
        <div style={{
          position: 'fixed',
          top: 32,
          right: 32,
          background: '#43a047',
          color: '#fff',
          padding: '16px 32px',
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '1.1rem',
          boxShadow: '0 2px 12px #000a',
          zIndex: 9999,
        }}>
          Registration successful! Redirecting to sign in...
        </div>
      )}
    </div>
  );
};

export default RegisterPage; 